import { makeStyles, tokens, Text } from '@fluentui/react-components';
import { EXTERNAL_LINKS, MOBILE_BREAKPOINT } from './constants';
import { OttoLogo } from '@/components/OttoLogo';
import { ottoTokens } from '@/theme/ottoTokens';

const useStyles = makeStyles({
  footer: {
    borderTopStyle: 'solid',
    borderTopWidth: '1px',
    borderTopColor: tokens.colorNeutralStroke2,
    backgroundColor: tokens.colorNeutralBackground2,
    paddingBlock: '60px',
  },
  inner: {
    maxWidth: '1200px',
    marginInline: 'auto',
    paddingInline: tokens.spacingHorizontalXXL,
    [`@media (max-width: ${MOBILE_BREAKPOINT})`]: {
      paddingInline: tokens.spacingHorizontalL,
    },
  },
  // Brand grows 2x the link columns and fills the leading space. Link
  // columns each take flex:1 so they distribute the remaining inline
  // space evenly — removing a column (e.g. Build and Company today,
  // where every link is still a TODO) just shrinks the link group
  // smaller without leaving a ghostly empty grid track.
  grid: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: tokens.spacingHorizontalXXL,
    marginBlockEnd: '48px',
    [`@media (max-width: ${MOBILE_BREAKPOINT})`]: {
      flexDirection: 'column',
      gap: tokens.spacingVerticalL,
    },
  },
  brandCol: {
    flex: '2 1 280px',
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalM,
  },
  brand: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
    color: tokens.colorNeutralForeground1,
    fontWeight: tokens.fontWeightSemibold,
    fontSize: tokens.fontSizeBase400,
    textDecorationLine: 'none',
  },
  tagline: {
    fontSize: tokens.fontSizeBase300,
    color: tokens.colorNeutralForeground3,
    maxWidth: '280px',
    lineHeight: 1.55,
  },
  col: {
    flex: '1 1 140px',
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalS,
  },
  colHeading: {
    fontSize: tokens.fontSizeBase200,
    fontWeight: tokens.fontWeightSemibold,
    textTransform: 'uppercase',
    letterSpacing: '0.08em',
    color: ottoTokens.landingDimText,
    marginBlockEnd: tokens.spacingVerticalS,
  },
  link: {
    color: tokens.colorNeutralForeground2,
    textDecorationLine: 'none',
    fontSize: tokens.fontSizeBase300,
    transitionProperty: 'color',
    transitionDuration: tokens.durationNormal,
    transitionTimingFunction: tokens.curveEasyEase,
    ':hover': {
      color: tokens.colorNeutralForeground1,
    },
    ':focus-visible': {
      outlineWidth: '2px',
      outlineStyle: 'solid',
      outlineColor: tokens.colorBrandStroke1,
      outlineOffset: '2px',
      borderRadius: tokens.borderRadiusSmall,
    },
    '@media (prefers-reduced-motion: reduce)': {
      transitionDuration: '0.01ms',
    },
  },
  bottom: {
    paddingBlockStart: tokens.spacingVerticalXL,
    borderTopStyle: 'solid',
    borderTopWidth: '1px',
    borderTopColor: tokens.colorNeutralStroke2,
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    color: ottoTokens.landingDimText,
    fontSize: tokens.fontSizeBase200,
    flexWrap: 'wrap',
    gap: tokens.spacingHorizontalM,
    [`@media (max-width: ${MOBILE_BREAKPOINT})`]: {
      flexDirection: 'column',
      textAlign: 'center',
    },
  },
});

interface FooterLink {
  readonly label: string;
  readonly href: string | undefined;
  readonly external?: boolean;
}

interface FooterColumn {
  readonly heading: string;
  readonly links: readonly FooterLink[];
}

const FOOTER_COLUMNS: readonly FooterColumn[] = [
  {
    heading: 'Product',
    links: [
      { label: 'Overview', href: EXTERNAL_LINKS.auto, external: true },
      { label: 'Connectors', href: undefined },
      { label: 'Pricing', href: EXTERNAL_LINKS.pricing },
      { label: 'Changelog', href: EXTERNAL_LINKS.changelog },
    ],
  },
  {
    heading: 'Build',
    links: [
      { label: 'Documentation', href: EXTERNAL_LINKS.docs },
      { label: 'Templates', href: EXTERNAL_LINKS.templates },
      { label: 'API reference', href: EXTERNAL_LINKS.apiReference },
      { label: 'SDK', href: EXTERNAL_LINKS.sdk },
      { label: 'MCP guide', href: EXTERNAL_LINKS.mcpGuide },
    ],
  },
  {
    heading: 'Company',
    links: [
      { label: 'About', href: EXTERNAL_LINKS.about },
      { label: 'Case studies', href: EXTERNAL_LINKS.caseStudies },
      { label: 'Blog', href: EXTERNAL_LINKS.blog },
      { label: 'Careers', href: EXTERNAL_LINKS.careers },
      { label: 'Press', href: EXTERNAL_LINKS.press },
    ],
  },
  {
    heading: 'Legal',
    links: [
      { label: 'Privacy', href: EXTERNAL_LINKS.privacy, external: true },
      { label: 'Terms', href: EXTERNAL_LINKS.terms, external: true },
      { label: 'Security', href: EXTERNAL_LINKS.security },
      { label: 'Trust center', href: EXTERNAL_LINKS.trustCenter },
    ],
  },
];

export const LandingFooter = () => {
  const styles = useStyles();
  const year = new Date().getFullYear();

  return (
    <footer className={styles.footer}>
      <div className={styles.inner}>
        <div className={styles.grid}>
          <div className={styles.brandCol}>
            <a href={EXTERNAL_LINKS.auto} className={styles.brand}>
              <OttoLogo size={24} />
              Logic Apps Automation
            </a>
            <Text className={styles.tagline}>
              Enterprise automation for any builder. From Microsoft.
            </Text>
          </div>
          {FOOTER_COLUMNS
            // Hide columns that have zero resolved links today — Build
            // and Company would otherwise render as empty headings.
            // The column reappears automatically as soon as any of its
            // TODO targets get a URL.
            .map((col) => ({
              col,
              resolved: col.links.filter(
                (l): l is FooterLink & { href: string } => Boolean(l.href),
              ),
            }))
            .filter(({ resolved }) => resolved.length > 0)
            .map(({ col, resolved }) => (
              <div key={col.heading} className={styles.col}>
                <Text className={styles.colHeading}>{col.heading}</Text>
                {resolved.map((l) => (
                  <a
                    key={l.label}
                    href={l.href}
                    className={styles.link}
                    {...(l.external
                      ? { target: '_blank', rel: 'noopener noreferrer' }
                      : {})}
                  >
                    {l.label}
                  </a>
                ))}
              </div>
            ))}
        </div>
        <div className={styles.bottom}>
          <Text>© {year} Microsoft Corporation. Logic Apps Automation.</Text>
          <Text>Built for builders · Powered by AI · Enterprise ready</Text>
        </div>
      </div>
    </footer>
  );
};
